/**
 * CRM Tasks page — all tasks or my tasks, with full create/edit modal.
 * Soft-delete: normal users move tasks to a "deleted" bin; admins can view
 * and permanently purge from the Deleted tab.
 */
import { useState, useEffect, useCallback } from 'react'
import { SortableHeader } from '../../components/SortableHeader'
import { useTableSort } from '../../hooks/useTableSort'
import { Pagination } from '../../components/Pagination'
import { Link } from 'react-router-dom'
import { format, parseISO } from 'date-fns'
import { listTasks, updateTask, deleteTask, createTask } from '../../services/crm'
import { listUsers } from '../../services/users'
import { useAuth } from '../../services/auth'
import type { Task, TaskPriority, TaskStatus, TaskCreate, TaskUpdate } from '../../types'
import { XIcon } from '../../components/Icons'
import Modal, { ModalFooter } from '../../components/Modal'
import FilterBar from '../../components/FilterBar'
import PageHeading from '../../components/PageHeading'
import { TASKS_HELP } from '../../content/pageHelp'
import { DUE_WINDOW_OPTIONS, PRIORITY_FILTER_OPTIONS, windowDays } from '../../utils/filterOptions'
import { userLabel } from '../../utils/userOptions'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import {
  isCode, isIdFilter, isPageNumber, listStateKey, oneOf, textUpTo, usePageSize, useSessionState,
} from '../../hooks/useSessionState'

const PRIORITY_COLORS: Record<TaskPriority, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
}

const NOTIFY_BEFORE_OPTIONS = [
  { label: 'No advance notice', value: '' },
  { label: '15 minutes before', value: '15' },
  { label: '30 minutes before', value: '30' },
  { label: '1 hour before', value: '60' },
  { label: '2 hours before', value: '120' },
  { label: '1 day before', value: '1440' },
]

interface UserOption { id: number; email: string; full_name: string | null }

interface TaskFormState {
  title: string
  description: string
  due_at: string
  priority: TaskPriority
  status: TaskStatus
  link_type: 'none' | 'client' | 'lead'
  client_id: string
  lead_id: string
  assigned_to_user_id: string
  notify_before_minutes: string
  notify_when_overdue: boolean
}

const EMPTY_FORM: TaskFormState = {
  title: '',
  description: '',
  due_at: '',
  priority: 'medium',
  status: 'open',
  link_type: 'none',
  client_id: '',
  lead_id: '',
  assigned_to_user_id: '',
  notify_before_minutes: '',
  notify_when_overdue: true,
}

function taskToForm(task: Task): TaskFormState {
  let due_at = ''
  if (task.due_at) {
    due_at = task.due_at.slice(0, 16)
  } else if (task.due_date) {
    due_at = task.due_date + 'T00:00'
  }
  return {
    title: task.title,
    description: task.description || '',
    due_at,
    priority: task.priority,
    status: task.status,
    link_type: task.lead_id ? 'lead' : task.client_id ? 'client' : 'none',
    client_id: task.client_id ? String(task.client_id) : '',
    lead_id: task.lead_id ? String(task.lead_id) : '',
    assigned_to_user_id: task.assigned_to_user_id ? String(task.assigned_to_user_id) : '',
    notify_before_minutes: task.notify_before_minutes ? String(task.notify_before_minutes) : '',
    notify_when_overdue: task.notify_when_overdue,
  }
}

function formToCreatePayload(form: TaskFormState): TaskCreate {
  const payload: TaskCreate = {
    title: form.title.trim(),
    priority: form.priority,
    notify_when_overdue: form.notify_when_overdue,
  }
  if (form.description.trim()) payload.description = form.description.trim()
  if (form.due_at) {
    payload.due_at = form.due_at + ':00'
    payload.due_date = form.due_at.slice(0, 10)
  }
  if (form.link_type === 'client' && form.client_id) payload.client_id = parseInt(form.client_id)
  if (form.link_type === 'lead' && form.lead_id) payload.lead_id = parseInt(form.lead_id)
  if (form.assigned_to_user_id) payload.assigned_to_user_id = parseInt(form.assigned_to_user_id)
  if (form.notify_before_minutes) payload.notify_before_minutes = parseInt(form.notify_before_minutes)
  return payload
}

function formToUpdatePayload(form: TaskFormState): TaskUpdate {
  const payload: TaskUpdate = {
    title: form.title.trim(),
    description: form.description.trim() || undefined,
    priority: form.priority,
    status: form.status,
    notify_when_overdue: form.notify_when_overdue,
  }
  if (form.due_at) {
    payload.due_at = form.due_at + ':00'
    payload.due_date = form.due_at.slice(0, 10)
  }
  payload.client_id = form.link_type === 'client' && form.client_id ? parseInt(form.client_id) : undefined
  payload.lead_id = form.link_type === 'lead' && form.lead_id ? parseInt(form.lead_id) : undefined
  payload.assigned_to_user_id = form.assigned_to_user_id ? parseInt(form.assigned_to_user_id) : undefined
  payload.notify_before_minutes = form.notify_before_minutes ? parseInt(form.notify_before_minutes) : undefined
  return payload
}

function PencilIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
    </svg>
  )
}

function TaskModal({
  titleId,
  title,
  form,
  setForm,
  onSubmit,
  onClose,
  error,
  submitting,
  users,
  isEdit,
}: {
  titleId: string
  title: string
  form: TaskFormState
  setForm: (f: TaskFormState) => void
  onSubmit: (e: React.FormEvent) => void
  onClose: () => void
  error: string
  submitting: boolean
  users: UserOption[]
  isEdit?: boolean
}) {
  return (
    <Modal titleId={titleId} title={title} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title <span className="text-red-500">*</span></label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            maxLength={200}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date & Time</label>
            <input
              type="datetime-local"
              value={form.due_at}
              onChange={(e) => setForm({ ...form, due_at: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value as TaskPriority })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
        </div>

        {isEdit && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as TaskStatus })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
          <select
            value={form.assigned_to_user_id}
            onChange={(e) => setForm({ ...form, assigned_to_user_id: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">Unassigned</option>
            {users.map((u) => (
              <option key={u.id} value={String(u.id)}>{u.full_name || u.email}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Link To</label>
          <div className="flex gap-4 mb-2">
            {(['none', 'client', 'lead'] as const).map((opt) => (
              <label key={opt} className="flex items-center gap-1.5 py-2 pr-2 min-h-[32px] text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  className="h-5 w-5"
                  value={opt}
                  checked={form.link_type === opt}
                  onChange={() => setForm({ ...form, link_type: opt, client_id: '', lead_id: '' })}
                />
                {opt === 'none' ? 'None' : opt === 'client' ? 'Client' : 'Lead'}
              </label>
            ))}
          </div>
          {form.link_type === 'client' && (
            <input
              type="number"
              placeholder="Client ID"
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              min={1}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          )}
          {form.link_type === 'lead' && (
            <input
              type="number"
              placeholder="Lead ID (FCC unique system identifier)"
              value={form.lead_id}
              onChange={(e) => setForm({ ...form, lead_id: e.target.value })}
              min={1}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notify Before Due</label>
          <select
            value={form.notify_before_minutes}
            onChange={(e) => setForm({ ...form, notify_before_minutes: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            {NOTIFY_BEFORE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 py-2 min-h-[32px] text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={form.notify_when_overdue}
            onChange={(e) => setForm({ ...form, notify_when_overdue: e.target.checked })}
            className="rounded h-5 w-5"
          />
          Notify when overdue
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <ModalFooter>
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50">
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50">
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

function formatDue(task: Task): { label: string; overdue: boolean } {
  const now = new Date()
  if (task.due_at) {
    const dt = parseISO(task.due_at)
    const overdue = dt < now && task.status !== 'completed' && task.status !== 'cancelled'
    return { label: format(dt, 'MM/dd/yyyy HH:mm'), overdue }
  }
  if (task.due_date) {
    const dt = parseISO(task.due_date)
    const overdue = dt < now && task.status !== 'completed' && task.status !== 'cancelled'
    return { label: format(dt, 'MM/dd/yyyy'), overdue }
  }
  return { label: '—', overdue: false }
}

type TabKey = 'all' | 'mine' | 'overdue' | 'unassigned' | 'deleted'

/** Must match `_TASK_SORTS` in backend/app/api/v1/crm.py — the backend rejects anything else. */
type SortKey = 'title' | 'status' | 'priority' | 'due' | 'created'
const SORT_KEYS: readonly SortKey[] = ['title', 'status', 'priority', 'due', 'created']
const TAB_KEYS: readonly TabKey[] = ['all', 'mine', 'overdue', 'unassigned', 'deleted']

// Tab, filters, page and sort are remembered for the tab (useSessionState), so opening
// a task's client or lead and coming Back finds the list as it was left.
const KEY = listStateKey('tasks')

// PRIORITY_ORDER lived here to make the client-side comparator sort by severity rather
// than alphabetically. Sorting is server-side now and that knowledge moved with it, into
// the `priority` CASE in `_TASK_SORTS` (backend/app/api/v1/crm.py) — one definition, and
// it is the one the database actually orders by.

export default function TasksPage() {
  const { user: currentUser } = useAuth()
  const isAdmin = currentUser?.is_superuser ?? false

  const [tasks, setTasks] = useState<Task[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useSessionState(KEY('page'), 1, isPageNumber)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [users, setUsers] = useState<UserOption[]>([])

  // "Deleted" is admin-only; a remembered one is dropped for anyone else.
  const [tab, setTab] = useSessionState<TabKey>(
    KEY('tab'), 'all', v => oneOf(TAB_KEYS)(v) && (v !== 'deleted' || isAdmin),
  )
  const [filterStatus, setFilterStatus] = useSessionState(KEY('status'), '', isCode)
  const [filterSearch, setFilterSearch] = useSessionState(KEY('search'), '', textUpTo(200))
  const [filterPriority, setFilterPriority] = useSessionState(KEY('priority'), '', isCode)
  const [filterDue, setFilterDue] = useSessionState(KEY('due'), '', isCode)
  // '' = anyone · a user id as a string. The My / Unassigned tabs still exist; this
  // answers the other question — "what is on Kent's list?" — which they cannot.
  const [filterAssignee, setFilterAssignee] = useSessionState(KEY('assignee'), '', isIdFilter)
  // Debounced so a typed query is one request, not one per keystroke.
  const debouncedSearch = useDebouncedValue(filterSearch)

  /** Any filter change re-reads page 1 — page 3 of the old list is not page 3 of the new one. */
  function applyFilter<T>(set: (v: T) => void) {
    return (value: T) => { set(value); setPage(1) }
  }
  const { sortKey, sortOrder: sortDir, handleSort: handleSortClick } = useTableSort<SortKey>(
    'due', 'asc',
    // `created` is a timestamp — newest first is what anyone clicking it means.
    {
      descFirst: ['created'], onSortChange: () => setPage(1),
      persist: { key: KEY('sort'), keys: SORT_KEYS },
    },
  )

  const myTasksOnly = tab === 'mine'
  const overdueOnly = tab === 'overdue'
  const unassignedOnly = tab === 'unassigned'
  const showDeleted = tab === 'deleted'

  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState<TaskFormState>(EMPTY_FORM)
  const [createError, setCreateError] = useState('')
  const [creating, setCreating] = useState(false)

  const [editTask, setEditTask] = useState<Task | null>(null)
  const [editForm, setEditForm] = useState<TaskFormState>(EMPTY_FORM)
  const [editError, setEditError] = useState('')
  const [editing, setEditing] = useState(false)

  const [pageSize, setPageSize] = usePageSize('tasks', 25)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      // When "All statuses" is selected (empty filterStatus), pass include_completed=true
      // so the list truly shows all tasks. When a specific status is selected, the
      // status filter alone is sufficient (completed tasks appear when selecting "Completed").
      const includeCompleted = !showDeleted && !filterStatus
      const res = await listTasks({
        page,
        page_size: pageSize,
        my_tasks: myTasksOnly || undefined,
        unassigned: unassignedOnly || undefined,
        assigned_to_user_id: filterAssignee ? parseInt(filterAssignee, 10) : undefined,
        status: (!showDeleted && filterStatus) ? filterStatus : undefined,
        priority: filterPriority || undefined,
        due_within_days: windowDays(filterDue),
        include_completed: includeCompleted || undefined,
        overdue_only: overdueOnly || undefined,
        show_deleted: showDeleted || undefined,
        search: debouncedSearch || undefined,
        sort: sortKey,
        order: sortDir,
      })
      setTasks(res.items)
      setTotal(res.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tasks')
    } finally {
      setLoading(false)
    }
  }, [page, myTasksOnly, unassignedOnly, filterAssignee, filterStatus, filterPriority,
      filterDue, overdueOnly, showDeleted, debouncedSearch, sortKey, sortDir, pageSize])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    listUsers().then((res) => setUsers(res.users)).catch(() => {})
  }, [])

  async function handleStatusChange(task: Task, newStatus: TaskStatus) {
    try {
      await updateTask(task.id, { status: newStatus })
      load()
    } catch { /* ignore */ }
  }

  async function handleDelete(taskId: number) {
    const isPermanent = showDeleted
    const msg = isPermanent
      ? 'Permanently delete this task? This cannot be undone.'
      : 'Move this task to the deleted bin?'
    if (!confirm(msg)) return
    try {
      await deleteTask(taskId, isPermanent)
      load()
    } catch { /* ignore */ }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!createForm.title.trim()) { setCreateError('Title is required'); return }
    setCreating(true)
    setCreateError('')
    try {
      await createTask(formToCreatePayload(createForm))
      setShowCreate(false)
      setCreateForm(EMPTY_FORM)
      setPage(1)
      load()
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create task')
    } finally {
      setCreating(false)
    }
  }

  async function handleEdit(e: React.FormEvent) {
    e.preventDefault()
    if (!editTask) return
    if (!editForm.title.trim()) { setEditError('Title is required'); return }
    setEditing(true)
    setEditError('')
    try {
      await updateTask(editTask.id, formToUpdatePayload(editForm))
      setEditTask(null)
      load()
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to update task')
    } finally {
      setEditing(false)
    }
  }

  const totalPages = Math.ceil(total / pageSize)

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'all',        label: 'All Tasks' },
    { key: 'mine',       label: 'My Tasks' },
    { key: 'overdue',    label: 'Overdue' },
    { key: 'unassigned', label: 'Unassigned' },
    ...(isAdmin ? [{ key: 'deleted' as TabKey, label: 'Deleted' }] : []),
  ]

  // Sorting is SERVER-side as of v0.41.4: this page used to run `[...tasks].sort()` over
  // one page of 25 and present the result as the sorted list. The toggle rule and the
  // page reset now live in useTableSort, shared with four other pages.

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <PageHeading title="Tasks" help={TASKS_HELP} subtitle={`${total} total`} />
        {!showDeleted && (
          <button
            onClick={() => { setShowCreate(true); setCreateError(''); setCreateForm(EMPTY_FORM) }}
            className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
          >
            + New Task
          </button>
        )}
      </div>

      {/* Tabs, then the filter bar underneath — the same two-row shape as Leads.
          They used to share one row here, which is why this page's filters sat at a
          different height and width from every other list page's. */}
      <div className="flex flex-wrap gap-4 border-b border-gray-200 mb-4">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => { setTab(key); setPage(1); setFilterStatus(''); setFilterDue('') }}
            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              tab === key
                ? key === 'deleted'
                  ? 'border-red-500 text-red-600'
                  : 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Search applies on every tab, Deleted included — the deleted bin is exactly
          where you go hunting for one specific task. Status is hidden there, where
          every row has the same state, and the due window is hidden on Overdue,
          where "due in the next 7 days" could only ever return nothing. */}
      <FilterBar
        searchValue={filterSearch}
        onSearchChange={applyFilter(setFilterSearch)}
        searchPlaceholder="Search tasks, client or lead…"
        searchLabel="Search tasks"
        selects={[
          ...(showDeleted ? [] : [{
            id: 'task-status',
            label: 'Filter tasks by status',
            value: filterStatus,
            onChange: applyFilter(setFilterStatus),
            options: [
              { value: '', label: 'All statuses' },
              { value: 'open', label: 'Open' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' },
            ],
          }]),
          {
            id: 'task-priority',
            label: 'Filter tasks by priority',
            value: filterPriority,
            onChange: applyFilter(setFilterPriority),
            options: PRIORITY_FILTER_OPTIONS,
          },
          ...(overdueOnly ? [] : [{
            id: 'task-due',
            label: 'Filter tasks by due window',
            value: filterDue,
            onChange: applyFilter(setFilterDue),
            options: DUE_WINDOW_OPTIONS,
          }]),
          {
            id: 'task-assignee',
            label: 'Filter tasks by assignee',
            value: filterAssignee,
            onChange: applyFilter(setFilterAssignee),
            options: [
              { value: '', label: 'Anyone' },
              ...users.map((u) => ({ value: String(u.id), label: userLabel(u) })),
            ],
          },
        ]}
      />

      {showDeleted && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          <strong>Deleted tasks</strong> — Permanently removing a task from this list cannot be undone.
          Tasks are auto-purged based on the retention setting in{' '}
          <Link to="/admin/settings" className="underline">General Settings</Link>.
        </div>
      )}

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>}

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : tasks.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {showDeleted
              ? 'No deleted tasks.'
              : <>No tasks found.{' '}
                  <button onClick={() => { setShowCreate(true); setCreateError(''); setCreateForm(EMPTY_FORM) }} className="text-primary-600 hover:underline">
                    Create one
                  </button>
                </>
            }
          </div>
        ) : (
          <div className="table-wrap">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader
                    sortKey="title" currentSortKey={sortKey} currentSortOrder={sortDir}
                    onSort={handleSortClick} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    Task
                  </SortableHeader>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Linked To</th>
                  <SortableHeader
                    sortKey="due" currentSortKey={sortKey} currentSortOrder={sortDir}
                    onSort={handleSortClick} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    Due
                  </SortableHeader>
                  <SortableHeader
                    sortKey="priority" currentSortKey={sortKey} currentSortOrder={sortDir}
                    onSort={handleSortClick} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell"
                  >
                    Priority
                  </SortableHeader>
                  {/* The Deleted tab replaces this column entirely, and a deleted-at column
                      is not one the backend can sort on — so it is a plain <th> there
                      rather than a header that looks clickable and does nothing. */}
                  {showDeleted ? (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deleted</th>
                  ) : (
                    <SortableHeader
                      sortKey="status" currentSortKey={sortKey} currentSortOrder={sortDir}
                      onSort={handleSortClick} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                    >
                      Status
                    </SortableHeader>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">
                    {showDeleted ? 'Deleted By' : 'Assigned'}
                  </th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {tasks.map((t) => {
                  const { label: dueLabel, overdue } = formatDue(t)
                  return (
                    <tr key={t.id} className={showDeleted ? 'bg-red-50/40' : overdue ? 'bg-red-50' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {showDeleted ? (
                            <span className={`text-sm font-medium text-gray-500 line-through`}>{t.title}</span>
                          ) : (
                            <button
                              onClick={() => { setEditTask(t); setEditForm(taskToForm(t)); setEditError('') }}
                              className={`text-sm font-medium text-left hover:text-primary-600 inline-flex items-center min-h-[32px] ${t.status === 'completed' ? 'line-through text-gray-400' : 'text-gray-900'}`}
                              title="Edit task"
                            >
                              {t.title}
                            </button>
                          )}
                          {overdue && !showDeleted && (
                            <span className="px-1.5 py-0.5 text-xs font-bold rounded bg-red-600 text-white">OVERDUE</span>
                          )}
                        </div>
                        {t.description && (
                          <p className="text-xs text-gray-400 truncate max-w-xs">{t.description}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                        {t.lead_id ? (
                          <Link to={`/crm/leads/${t.lead_id}`} className="text-primary-600 hover:underline">
                            {t.lead_entity_name || `Lead #${t.lead_id}`}
                          </Link>
                        ) : t.client_id ? (
                          <Link to={`/crm/clients/${t.client_id}`} className="text-primary-600 hover:underline">
                            {t.client_name || `Client #${t.client_id}`}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={overdue && !showDeleted ? 'text-red-600 font-medium' : 'text-gray-700'}>
                          {dueLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                      </td>
                      <td className="px-4 py-3">
                        {showDeleted ? (
                          <span className="text-xs text-gray-500">
                            {t.deleted_at ? format(parseISO(t.deleted_at), 'MM/dd/yyyy') : '—'}
                          </span>
                        ) : (
                          <select
                            value={t.status}
                            onChange={(e) => handleStatusChange(t, e.target.value as TaskStatus)}
                            aria-label={`Task status for ${t.title}`}
                            className="text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">
                        {showDeleted
                          ? (t.deleted_by_user_id ? `User #${t.deleted_by_user_id}` : '—')
                          : (t.assigned_to?.full_name || t.assigned_to?.email || '—')
                        }
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {!showDeleted && (
                            <button
                              onClick={() => { setEditTask(t); setEditForm(taskToForm(t)); setEditError('') }}
                              className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                              title="Edit task"
                              aria-label={`Edit task: ${t.title}`}
                            >
                              <PencilIcon />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(t.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                            title={showDeleted ? 'Permanently delete' : 'Delete task'}
                            aria-label={showDeleted ? `Permanently delete: ${t.title}` : `Delete task: ${t.title}`}
                          >
                            <XIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      <Pagination
        page={page} totalPages={totalPages} onPageChange={setPage}
        total={total} pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1) }}
      />

      {/* Create Modal */}
      {showCreate && (
        <TaskModal
          titleId="task-create-title"
          title="New Task"
          form={createForm}
          setForm={setCreateForm}
          onSubmit={handleCreate}
          onClose={() => { setShowCreate(false); setCreateForm(EMPTY_FORM) }}
          error={createError}
          submitting={creating}
          users={users}
          isEdit={false}
        />
      )}

      {/* Edit Modal */}
      {editTask && (
        <TaskModal
          titleId="task-edit-title"
          title="Edit Task"
          form={editForm}
          setForm={setEditForm}
          onSubmit={handleEdit}
          onClose={() => setEditTask(null)}
          error={editError}
          submitting={editing}
          users={users}
          isEdit={true}
        />
      )}
    </div>
  )
}
